import { useMemo } from "react";
import { Link as RouterLink, useLocation } from "react-router-dom";
import {
  Box,
  Button,
  Heading,
  HStack,
  IconButton,
  Stack,
  Text,
} from "@chakra-ui/react";
import { Helmet } from "react-helmet-async";
import { FaFacebookF, FaLinkedinIn, FaTwitter } from "react-icons/fa";
import { FiCopy } from "react-icons/fi";

const toReadableStatus = (value) => {
  const normalized = `${value || ""}`.trim();
  if (!normalized) return "";
  return normalized
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const DonationThankYou = () => {
  const location = useLocation();

  const { donationId, paymentStatus, shareUrl } = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const fromQuery = `${params.get("shareUrl") || ""}`.trim();
    const fallback =
      typeof window === "undefined" ? "" : `${window.location.origin}`;
    const normalizedShareUrl = /^https?:\/\//i.test(fromQuery)
      ? fromQuery
      : fallback;

    return {
      donationId: params.get("donationId") || "",
      paymentStatus: toReadableStatus(params.get("status")),
      shareUrl: normalizedShareUrl,
    };
  }, [location.search]);

  const shareMessage =
    "I just donated through PurelyGive. Join me in supporting this cause.";
  const encodedShareUrl = encodeURIComponent(shareUrl || "");
  const encodedMessage = encodeURIComponent(shareMessage);

  const shareTargets = {
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedShareUrl}`,
    twitter: `https://twitter.com/intent/tweet?url=${encodedShareUrl}&text=${encodedMessage}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedShareUrl}`,
  };

  const handleCopy = async () => {
    if (!shareUrl || !navigator?.clipboard?.writeText) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
    } catch {
      // Ignore clipboard failures and keep the page usable.
    }
  };

  return (
    <Box maxW="720px" mx="auto" p={{ base: 6, md: 10 }}>
      <Helmet>
        <title>Thank You - PurelyGive</title>
        <meta
          name="description"
          content="Thank you for supporting this nonprofit through PurelyGive."
        />
      </Helmet>

      <Stack
        spacing={4}
        bg="white"
        borderWidth="1px"
        borderRadius="xl"
        p={{ base: 6, md: 8 }}
        boxShadow="sm"
      >
        <Heading size="lg">Thank you for your donation.</Heading>
        <Text color="gray.700">
          Your support helps this nonprofit keep making an impact.
        </Text>
        {donationId ? <Text>Donation ID: {donationId}</Text> : null}
        {paymentStatus ? <Text>Payment Status: {paymentStatus}</Text> : null}
        <Stack spacing={3} pt={2}>
          <Text fontWeight="600">Share this donation page</Text>
          <HStack spacing={3}>
            <IconButton
              as="a"
              href={shareTargets.facebook}
              target="_blank"
              rel="noopener noreferrer"
              icon={<FaFacebookF />}
              aria-label="Share on Facebook"
              isDisabled={!shareUrl}
            />
            <IconButton
              as="a"
              href={shareTargets.twitter}
              target="_blank"
              rel="noopener noreferrer"
              icon={<FaTwitter />}
              aria-label="Share on Twitter"
              isDisabled={!shareUrl}
            />
            <IconButton
              as="a"
              href={shareTargets.linkedin}
              target="_blank"
              rel="noopener noreferrer"
              icon={<FaLinkedinIn />}
              aria-label="Share on LinkedIn"
              isDisabled={!shareUrl}
            />
            <Button
              leftIcon={<FiCopy />}
              variant="outline"
              onClick={handleCopy}
              isDisabled={!shareUrl}
            >
              Copy URL
            </Button>
          </HStack>
        </Stack>
        <Stack direction={{ base: "column", sm: "row" }} spacing={3} pt={2}>
          <Button as={RouterLink} to="/" colorScheme="orange">
            Back to Home
          </Button>
          <Button as={RouterLink} to="/donor/account" variant="outline">
            View Donor Account
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
};

export default DonationThankYou;
